import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFicheEmploye } from "../../../_api/Document";
import { getEstablishments } from "../../../_api/Establishment";
import { getTeamEstablishments } from "../../../_api/Team";
import { getUsers } from "../../../_api/Admin";
import {
    getExportProfiles,
    generatePayrollExport,
} from "../../../_api/ExportProfile";
import { Button } from "../../../_helpers/components/Button";
import { DefaultDropdown } from "../../../_helpers/components/Forms/Dropdown";
import toast from "react-hot-toast";

const TABS = [
    { id: "run", label: "Lancer un export" },
    { id: "profiles", label: "Profils d'export" },
];

export const ExportView = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("run");

    const [establishments, setEstablishments] = useState([]);
    const [teams, setTeams] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemType, setItemType] = useState("");
    const [selectedProfileId, setSelectedProfileId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [isCsvExporting, setIsCsvExporting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [establishmentsResp, usersResp, profilesResp] = await Promise.all([
                getEstablishments(),
                getUsers(),
                getExportProfiles(),
            ]);

            setEstablishments(establishmentsResp.data || []);
            setEmployees(usersResp.data || []);
            setProfiles(profilesResp.data || []);

            const allTeams = [];
            for (const establishment of establishmentsResp.data || []) {
                try {
                    const teamsResp = await getTeamEstablishments(establishment.id);
                    if (teamsResp.data) {
                        allTeams.push(...teamsResp.data);
                    }
                } catch (error) {
                    console.error(error);
                }
            }
            setTeams(allTeams);

            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            setStartDate(firstDay.toISOString().split("T")[0]);
            setEndDate(lastDay.toISOString().split("T")[0]);
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors du chargement des données.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedItem(null);
            setItemType("");
            return;
        }
        const [id, type] = value.split("|");
        setSelectedItem(findItemByIdAndType(id, type));
        setItemType(type);
    };

    const findItemByIdAndType = (id, type) => {
        switch (type) {
            case "establishment":
                return establishments.find((e) => e.id === parseInt(id));
            case "team":
                return teams.find((t) => t.id === parseInt(id));
            case "employee":
                return employees.find((u) => u.id === parseInt(id));
            default:
                return null;
        }
    };

    const getDisplayName = (item, type) => {
        if (!item) return "";
        switch (type) {
            case "establishment":
                return item.name || `Établissement #${item.id}`;
            case "team":
                return item.name || `Équipe #${item.id}`;
            case "employee":
                return `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.email;
            default:
                return "";
        }
    };

    const scopeTypeForItem = (type) => {
        if (type === "employee") return "employee";
        if (type === "team") return "team";
        return "establishment";
    };

    const handleExportEmployeePdf = async () => {
        if (!selectedItem || itemType !== "employee" || isExporting) return;

        setIsExporting(true);
        try {
            const resp = await getFicheEmploye(selectedItem.id);
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `fiche_employe_${selectedItem.first_name}_${selectedItem.last_name}.pdf`;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            toast.error("Erreur lors de l'export PDF.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleCsvExport = async () => {
        if (!selectedProfileId || !selectedItem || !startDate || !endDate || isCsvExporting) {
            toast.error("Sélectionnez un profil, une période et un périmètre.");
            return;
        }

        setIsCsvExporting(true);
        try {
            const resp = await generatePayrollExport(selectedProfileId, {
                start_date: startDate,
                end_date: endDate,
                scope_type: scopeTypeForItem(itemType),
                scope_id: selectedItem.id,
            });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `export_paie_${startDate}_${endDate}.csv`;
            link.click();
            window.URL.revokeObjectURL(link.href);
            toast.success("Export téléchargé.");
        } catch (error) {
            toast.error("Erreur lors de la génération de l'export.");
        } finally {
            setIsCsvExporting(false);
        }
    };

    const profileOptions = profiles.map((p) => ({
        value: p.id,
        label: p.name,
    }));

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <h1 className="text-3xl font-bold text-gray-900">Exports</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Export paie CSV et documents individuels
                    </p>
                    <div className="mt-4 flex gap-2 border-b border-gray-200">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={[
                                    "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                                    activeTab === tab.id
                                        ? "border-app-primary text-app-primary"
                                        : "border-transparent text-gray-500 hover:text-gray-700",
                                ].join(" ")}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary" />
                    </div>
                ) : activeTab === "run" ? (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
                            <h2 className="text-xl font-bold text-gray-900">Export paie CSV</h2>

                            <DefaultDropdown
                                name="Profil d'export"
                                value={selectedProfileId}
                                options={[
                                    { value: "", label: "-- Choisir un profil --" },
                                    ...profileOptions,
                                ]}
                                onChange={(e) => setSelectedProfileId(e.target.value)}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date de début
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date de fin
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Périmètre
                                </label>
                                <select
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                                    onChange={handleChange}
                                    value={selectedItem ? `${selectedItem.id}|${itemType}` : ""}
                                >
                                    <option value="">-- Choisir --</option>
                                    {establishments.length > 0 && (
                                        <optgroup label="Établissements">
                                            {establishments.map((est) => (
                                                <option key={est.id} value={`${est.id}|establishment`}>
                                                    {getDisplayName(est, "establishment")}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {teams.length > 0 && (
                                        <optgroup label="Équipes">
                                            {teams.map((team) => (
                                                <option key={team.id} value={`${team.id}|team`}>
                                                    {getDisplayName(team, "team")}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {employees.length > 0 && (
                                        <optgroup label="Salariés">
                                            {employees.map((emp) => (
                                                <option key={emp.id} value={`${emp.id}|employee`}>
                                                    {getDisplayName(emp, "employee")}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                            </div>

                            <Button
                                action={handleCsvExport}
                                type="empty"
                                name="Télécharger le CSV"
                                loading={isCsvExporting}
                            />
                        </div>

                        {selectedItem && itemType === "employee" && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Fiche d'heures PDF — {getDisplayName(selectedItem, "employee")}
                                </h3>
                                <Button
                                    action={handleExportEmployeePdf}
                                    type="empty"
                                    name="Exporter la fiche d'heures"
                                    loading={isExporting}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Profils d'export</h2>
                            <Button
                                action={() => navigate("/company/exports/profiles/add")}
                                name="Nouveau profil"
                            />
                        </div>

                        {profiles.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">
                                Aucun profil d'export. Créez-en un pour commencer.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {profiles.map((profile) => (
                                    <div
                                        key={profile.id}
                                        onClick={() =>
                                            navigate(`/company/exports/profiles/edit/${profile.id}`)
                                        }
                                        className="cursor-pointer rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                                    >
                                        <h3 className="font-semibold text-gray-900">{profile.name}</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {profile.layout_mode === "multi_column"
                                                ? "Multicolonnes"
                                                : "1 ligne / enregistrement"}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-2">
                                            {profile.calculations_count ?? 0} calcul(s) ·{" "}
                                            {profile.columns_count ?? 0} colonne(s)
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
